import * as React from 'react'

import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Flex, Text } from '@chakra-ui/react'

import {
  SideMenuLayoutBreadcrumb,
  SideMenuLayoutContentLayout,
  Tag,
} from '@enechain/ecloud-designsystem'

import { ApiKeyRole } from '~/gen/proto/bff/v1/model_pb'
import { DescriptionList } from '~/pages/ApiKeys/components/DescriptionList'
import { RoleEndpointsTable } from '~/pages/ApiKeys/components/RoleEndpointsTable'
import { useApiKeyRole } from '~/pages/ApiKeys/hooks/useApiKeyRole'

const Stat: React.FC<{ value: number; label: string }> = ({ value, label }) => {
  return (
    <Flex flexDirection="column">
      <Text textStyle="headerLg" color="Text.highEmphasis">
        {value}
      </Text>
      <Text textStyle="textSm" color="Text.lowEmphasis">
        {label}
      </Text>
    </Flex>
  )
}

const RoleDetailBody: React.FC<{ role: ApiKeyRole }> = ({ role }) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'pages.api_keys.role',
  })
  return (
    <>
      <DescriptionList
        items={[
          {
            label: t('label.name'),
            value: (
              <Text textStyle="textMd" fontWeight="bold">
                {role.name}
              </Text>
            ),
          },
          {
            label: t('label.description'),
            value: <Text textStyle="textMd">{role.description}</Text>,
          },
          {
            label: t('label.scope'),
            value: (
              <Tag
                color="Text.accentPrimary"
                backgroundColor="Surface.accentPrimaryLightLow"
              >
                {role.scopeName}
              </Tag>
            ),
          },
          {
            label: t('label.operations'),
            value: (
              <Stat value={role.endpoints.length} label={t('stat.endpoints')} />
            ),
          },
        ]}
      />
      <Flex flexDirection="column" gap="1" marginTop="3">
        <Text textStyle="headerMd" color="Text.highEmphasis">
          {t('endpoints.title')}
        </Text>
        <Text textStyle="textSm" color="Text.midEmphasis">
          {t('endpoints.description')}
        </Text>
      </Flex>
      <RoleEndpointsTable endpoints={role.endpoints} />
    </>
  )
}

export const ApiKeyRoleDetail: React.FC<{ roleId: string }> = ({ roleId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const role = useApiKeyRole(roleId)

  return (
    <SideMenuLayoutContentLayout
      header={
        <Flex alignItems="center" justifyContent="space-between" width="full">
          <SideMenuLayoutBreadcrumb
            items={[
              {
                label: t('pages.api_keys.title'),
                onClick: () => {
                  void navigate({ to: '/api_keys' })
                },
              },
              {
                label: t('pages.api_keys.role.breadcrumb'),
                bold: true,
              },
            ]}
          />
        </Flex>
      }
    >
      <Flex
        flexDirection="column"
        gap="3"
        maxWidth="1280px"
        margin="auto"
        padding="2"
      >
        <Flex flexDirection="column" gap="1">
          <Text textStyle="headerLg" color="Text.highEmphasis">
            {role.name}
          </Text>
          <Text textStyle="textMd" color="Text.midEmphasis">
            {t('pages.api_keys.role.description')}
          </Text>
        </Flex>
        <RoleDetailBody role={role} />
      </Flex>
    </SideMenuLayoutContentLayout>
  )
}
